import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { lotKeys } from "@/features/lots/hooks/use-lots"
import {
  deleteUsinage,
  fabricateLotPlanned,
  patchUsinageLoads,
  rewindCouplage,
  saveCouplageDraft,
  scheduleUsinage,
  startUsinage,
  submitCouplage,
  updateUsinageWaste,
} from "@/features/milling/api/milling.api"
import type {
  CouplagePlanPayload,
  FabricateLotPlannedPayload,
  PatchUsinageLoadsPayload,
  ScheduleUsinagePayload,
  UpdateUsinageWastePayload,
} from "@/features/milling/api/types"
import { millingKeys } from "@/features/milling/hooks/use-milling"
import { millFloorKeys } from "@/features/mill-floor/hooks/use-mill-floor"
import { extractApiErrorMessage } from "@/lib/api/client"

function useMillingInvalidator() {
  const queryClient = useQueryClient()
  return (usinageId?: string | number) => {
    queryClient.invalidateQueries({ queryKey: millingKeys.all })
    queryClient.invalidateQueries({ queryKey: millFloorKeys.all })
    queryClient.invalidateQueries({ queryKey: lotKeys.all })
    if (usinageId) {
      queryClient.invalidateQueries({
        queryKey: millingKeys.detail(usinageId),
      })
    }
  }
}

export function useScheduleUsinage() {
  const { t } = useTranslation("milling")
  const invalidate = useMillingInvalidator()
  return useMutation({
    mutationFn: (payload: ScheduleUsinagePayload) => scheduleUsinage(payload),
    onSuccess: () => {
      toast.success(t("toast.scheduled"))
      invalidate()
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}

export function useStartUsinage() {
  const invalidate = useMillingInvalidator()
  return useMutation({
    mutationFn: (usinageId: string | number) => startUsinage(usinageId),
    onSuccess: (_data, usinageId) => {
      invalidate(usinageId)
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}

export function usePatchUsinageLoads(usinageId: string | number) {
  const { t } = useTranslation("milling")
  const invalidate = useMillingInvalidator()
  return useMutation({
    mutationFn: (payload: PatchUsinageLoadsPayload) =>
      patchUsinageLoads(usinageId, payload),
    onSuccess: () => {
      toast.success(t("toast.loadsUpdated"))
      invalidate(usinageId)
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}

// Step 1 mutation. On success the usinage is in COUPLAGE_PLANNED and
// the chargement remainders are visible again in mill-floor's
// "available loads" list — invalidating millFloorKeys ensures the
// schedule dialog picks them up on the next open.
export function useSubmitCouplage(usinageId: string | number) {
  const { t } = useTranslation("milling")
  const invalidate = useMillingInvalidator()
  return useMutation({
    mutationFn: (payload: CouplagePlanPayload) =>
      submitCouplage(usinageId, payload),
    onSuccess: () => {
      toast.success(t("toast.couplagePlanned"))
      invalidate(usinageId)
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}

// Draft variant of useSubmitCouplage. Saves the in-progress plan
// without transitioning status — operator stays on the planning view
// with their work persisted.
export function useSaveCouplageDraft(usinageId: string | number) {
  const { t } = useTranslation("milling")
  const invalidate = useMillingInvalidator()
  return useMutation({
    mutationFn: (payload: CouplagePlanPayload) =>
      saveCouplageDraft(usinageId, payload),
    onSuccess: () => {
      toast.success(t("toast.draftSaved"))
      invalidate(usinageId)
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}

// Rewinds COUPLAGE_PLANNED back to RUNNING so the operator can edit
// the plan. Allowed only before any Lot Planned has been fabricated.
export function useRewindCouplage(usinageId: string | number) {
  const { t } = useTranslation("milling")
  const invalidate = useMillingInvalidator()
  return useMutation({
    mutationFn: () => rewindCouplage(usinageId),
    onSuccess: () => {
      toast.success(t("toast.rewound"))
      invalidate(usinageId)
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}

// Step 2 mutation. One call per Lot Planned. On success the planned
// entry's status flips and (if it was the last pending entry) the
// usinage moves to COMPLETED.
export function useFabricateLotPlanned(
  usinageId: string | number,
  lotPlannedId: string,
) {
  const invalidate = useMillingInvalidator()
  return useMutation({
    mutationFn: (payload: FabricateLotPlannedPayload) =>
      fabricateLotPlanned(usinageId, lotPlannedId, payload),
    onSuccess: () => {
      invalidate(usinageId)
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}

// Records the operator-reported milling waste (déchet) for the whole
// run. Surfaced on the COMPLETED view's Freinte panel; the new value
// flows through the cached usinage detail so the KPI strip and the
// Freinte rapport refresh in one round-trip.
export function useUpdateUsinageWaste(usinageId: string | number) {
  const { t } = useTranslation("milling")
  const invalidate = useMillingInvalidator()
  return useMutation({
    mutationFn: (payload: UpdateUsinageWastePayload) =>
      updateUsinageWaste(usinageId, payload),
    onSuccess: () => {
      toast.success(t("toast.wasteUpdated"))
      invalidate(usinageId)
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}

export function useDeleteUsinage() {
  const { t } = useTranslation("milling")
  const invalidate = useMillingInvalidator()
  return useMutation({
    mutationFn: (usinageId: string | number) => deleteUsinage(usinageId),
    onSuccess: () => {
      toast.success(t("toast.deleted"))
      invalidate()
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}
